// Installation eligibility — state-rule driven.
//
// SOURCE OF TRUTH
// Per-state installation rules live in the `state_install_rules` table and are
// editable by admins at /admin/state-rules. This module holds the evaluation
// logic plus a built-in fallback copy of the seeded rules, so a failed query
// can never accidentally open installation everywhere (or close the core area).
//
// NOT related to this file: `location_overrides.min_linear_feet` (estimator
// minimum on marketing location pages), the 150 ft order configurator floor,
// and the 50 ft estimator slider floor.

/**
 * States whose whole territory is core service area for every service.
 *
 * This is a code-level guarantee that outranks `state_install_rules`: a row
 * for one of these states cannot narrow coverage. The admin UI must therefore
 * never present core states as if they could be switched off.
 */
export const CORE_COVERAGE_STATES = ["CT", "MA", "RI"] as const;
const FULL_STATE_COVERAGE = new Set<string>(CORE_COVERAGE_STATES);

/** True when a state's coverage is guaranteed in code and not rule-editable. */
export function isCoreCoverageState(state: string): boolean {
  return FULL_STATE_COVERAGE.has(normalizeState(state));
}

// Map common full state names → 2-letter abbreviation so mobile autofill
// (which often pastes "Connecticut" or "new york") still works.
const STATE_NAME_TO_ABBR: Record<string, string> = {
  CONNECTICUT: "CT", MASSACHUSETTS: "MA", "RHODE ISLAND": "RI", "NEW YORK": "NY",
  NEWYORK: "NY", "NEW-YORK": "NY", VERMONT: "VT",
};

/** Normalize anything a user types into a 2-letter US state code. */
export function normalizeState(input: string): string {
  const raw = (input || "").trim().toUpperCase();
  if (!raw) return "";
  if (raw.length === 2) return raw;
  if (STATE_NAME_TO_ABBR[raw]) return STATE_NAME_TO_ABBR[raw];
  // Last resort: first two letters (preserves prior behavior).
  return raw.slice(0, 2);
}

/**
 * Core service area — covers every service we sell, including labor-only work.
 * States that only have physical-fence installer coverage (e.g. VT) are NOT
 * here; they are expressed as rows in `state_install_rules`.
 */
export function isInServiceArea(state: string, _zip?: string): boolean {
  return FULL_STATE_COVERAGE.has(normalizeState(state));
}

/* ------------------------------------------------------------------------ *
 * Per-state installation rules
 * ------------------------------------------------------------------------ */

export interface StateInstallRule {
  state: string;
  /** 0 = no state minimum (install at any otherwise-valid size). */
  min_linear_feet: number;
  physical_install_enabled: boolean;
  labor_install_enabled: boolean;
}

export type StateInstallRules = Record<string, StateInstallRule>;

/**
 * Fallback copy of the seeded `state_install_rules` rows. Used when the live
 * query is unavailable so behavior degrades to the known-good configuration.
 */
export const DEFAULT_STATE_INSTALL_RULES: StateInstallRules = {
  CT: { state: "CT", min_linear_feet: 0, physical_install_enabled: true, labor_install_enabled: true },
  MA: { state: "MA", min_linear_feet: 0, physical_install_enabled: true, labor_install_enabled: true },
  RI: { state: "RI", min_linear_feet: 0, physical_install_enabled: true, labor_install_enabled: true },
  VT: { state: "VT", min_linear_feet: 200, physical_install_enabled: true, labor_install_enabled: false },
};

/** Minimum linear feet for installation in states with no configured rule. */
export const NATIONWIDE_INSTALL_MIN_FEET = 1000;

export function getStateRule(
  state: string,
  rules: StateInstallRules = DEFAULT_STATE_INSTALL_RULES,
): StateInstallRule | null {
  return rules[normalizeState(state)] ?? null;
}

/**
 * Minimum linear feet required for installation at this address, or null when
 * installation is not available at any size (e.g. labor-only outside coverage).
 * 0 means no minimum.
 */
export function installMinFeet(
  state: string,
  zip: string,
  productQualifies: boolean,
  rules: StateInstallRules = DEFAULT_STATE_INSTALL_RULES,
): number | null {
  // Full-coverage states: no size condition.
  if (isInServiceArea(state, zip)) return 0;

  const rule = getStateRule(state, rules);
  if (rule) {
    const enabled = productQualifies ? rule.physical_install_enabled : rule.labor_install_enabled;
    return enabled ? Math.max(0, rule.min_linear_feet) : null;
  }

  // Unconfigured state: physical fence products fall back to the national
  // large-project threshold. Labor-only services stay geographically limited.
  return productQualifies ? NATIONWIDE_INSTALL_MIN_FEET : null;
}

/**
 * Does this address + project qualify for professional installation?
 *
 * @param state        Raw or normalized US state.
 * @param zip          5-digit ZIP.
 * @param linearFeet   Project length in linear feet.
 * @param productQualifies  True for physical fence products (dog/deer/garden).
 *                          Labor-only services (restoration, installation_only)
 *                          need labor_install_enabled for that state.
 */
export function isInstallationEligible(
  state: string,
  zip: string,
  linearFeet: number,
  productQualifies: boolean,
  rules: StateInstallRules = DEFAULT_STATE_INSTALL_RULES,
): boolean {
  const min = installMinFeet(state, zip, productQualifies, rules);
  if (min === null) return false;
  if (min === 0) return true;
  return Number.isFinite(linearFeet) && linearFeet >= min;
}

/** Zero-foot gate installs require explicit unrestricted physical AND labor coverage.
 * Positive fence thresholds are never waived for a standalone gate. */
export function isGateInstallationEligible(
  state: string,
  rules: StateInstallRules = DEFAULT_STATE_INSTALL_RULES,
): boolean {
  if (isCoreCoverageState(state)) return true;
  const rule = getStateRule(state, rules);
  return !!rule && rule.physical_install_enabled && rule.labor_install_enabled && rule.min_linear_feet === 0;
}

/** True when eligibility comes from a size threshold rather than plain geography. */
export function isExtendedAreaInstall(
  state: string,
  zip: string,
  linearFeet: number,
  productQualifies: boolean,
  rules: StateInstallRules = DEFAULT_STATE_INSTALL_RULES,
): boolean {
  const min = installMinFeet(state, zip, productQualifies, rules);
  if (min === null || min === 0) return false;
  return isInstallationEligible(state, zip, linearFeet, productQualifies, rules);
}

/* ------------------------------------------------------------------------ *
 * Customer-facing copy
 * ------------------------------------------------------------------------ */

export const SERVICE_AREA_LABEL =
  "Serving Connecticut, Massachusetts, Rhode Island, New York and Vermont, subject to project eligibility and configured state minimums.";

export const EXTENDED_AREA_LABEL = "Extended-area installation";

/** Message shown when an order qualifies via a state/national size minimum. */
export function extendedAreaQualifiesMessage(minFeet: number): string {
  return `Your project qualifies for installation because it meets the ${minFeet.toLocaleString()}-foot minimum for this state. Availability and final scheduling are confirmed during your consultation.`;
}

/** Message shown when installation needs more linear feet than ordered. */
export function installMinimumMessage(minFeet: number): string {
  return `Professional installation at this address starts at ${minFeet.toLocaleString()} linear feet. Your project is below that, so we'll ship your fence materials instead.`;
}

/* --- Reason-specific checkout copy ------------------------------------- *
 * Two very different situations must never share one sentence:
 *   1. the project is simply shorter than the state's installation minimum
 *      (a bigger project at the same address WOULD install), and
 *   2. we don't offer this service type in this state at any size.
 * ----------------------------------------------------------------------- */

export type InstallUnavailableReason = "below_minimum" | "service_not_offered";

/** Why installation is unavailable, given the resolved state minimum. */
export function installUnavailableReason(
  stateMinFeet: number | null,
): InstallUnavailableReason {
  return stateMinFeet === null ? "service_not_offered" : "below_minimum";
}

export const BELOW_MINIMUM_HEADLINE =
  "This project is below the installation minimum for this state.";

export function belowInstallMinimumMessage(minFeet: number): string {
  return `We install in this state from ${minFeet.toLocaleString()} linear feet. A larger project at the same address would qualify — this one is under the minimum, so we'll ship your fence materials instead.`;
}

export const SERVICE_NOT_OFFERED_HEADLINE =
  "We don't offer this service in this state.";

/**
 * @param serviceLabel e.g. "Fence Restoration" — the specific service, so the
 * customer can tell a service-type limit apart from a project-size limit.
 */
export function serviceNotOfferedMessage(serviceLabel: string): string {
  return `${serviceLabel} is a labor-only service and isn't available in this state at any project size — this isn't about the size of your project.`;
}

export const EXTENDED_AREA_POLICY_SENTENCE =
  `Outside that area, dog, deer and garden fence projects that meet the minimum project size for their state — ${NATIONWIDE_INSTALL_MIN_FEET.toLocaleString()} linear feet by default — may qualify for extended-area installation, subject to review.`;

export const EXTENDED_AREA_QUALIFIES_MESSAGE = extendedAreaQualifiesMessage(
  NATIONWIDE_INSTALL_MIN_FEET,
);

/**
 * Short coverage label for headers/footers/marketing copy. Must never imply
 * unrestricted installation outside the core states — installation elsewhere
 * depends on the live per-state rules and project size.
 */
export const SERVICE_AREA_SHORT_LABEL =
  "Serving Connecticut, Massachusetts, Rhode Island, New York & Vermont — subject to project eligibility";

/**
 * Same coverage, phrased as a noun fragment so callers can embed it after
 * their own verb ("Serving …", "We install across …").
 */
export const SERVICE_AREA_SHORT_FRAGMENT =
  "Connecticut, Massachusetts, Rhode Island, New York & Vermont — subject to project eligibility";

/** Materials are shippable anywhere even where installation is unavailable. */
export const MATERIALS_SHIPPING_SENTENCE =
  "Fence materials ship nationwide, even where professional installation isn't available.";

/**
 * Accurate one-paragraph availability sentence for a marketing/location page,
 * derived from the LIVE rules rather than a number typed into the page. If an
 * admin changes a state minimum at /admin/state-rules, this copy follows.
 *
 * Always evaluated for physical fence products (dog/deer/garden), which is
 * what location pages market.
 */
export function locationInstallAvailabilitySentence(
  state: string,
  rules: StateInstallRules = DEFAULT_STATE_INSTALL_RULES,
  stateLabel?: string,
): string {
  const label = stateLabel || normalizeState(state) || "this state";
  const min = installMinFeet(state, "", true, rules);
  if (min === null) {
    return `Fence materials ship to ${label}. Professional installation isn't currently offered there — ${MATERIALS_SHIPPING_SENTENCE.charAt(0).toLowerCase()}${MATERIALS_SHIPPING_SENTENCE.slice(1)}`;
  }
  if (min === 0) {
    return `${label} is core service area — we install dog, deer and garden fences at any qualifying project size.`;
  }
  return `Professional installation in ${label} is available for dog, deer and garden fence projects of ${min.toLocaleString()} linear feet or more. Smaller projects aren't installed, but we ship the same fence materials to your address.`;
}
